import CommonValues from '../../resources/common-values.js';
import { toImmutable,Immutable } from 'nuclear-js';
import { findCurrentTerm } from '../../utils/terms.js';
import GrowthGoalsUtils from '../../components/student-profile/modules/growth-goals/utils';

// These utilities used to be in `app/data/utils.js` but were moved here to avoid circular dependencies.
//      That file imports student getters and student getters used to import that file.
export const findNextStudent = function(studentList, studentBid) {
    if (!studentList || !studentBid) {
        return null;
    } else if (studentList.size < 2) {
        return CommonValues.EMPTY_STUDENT_PLACEHOLDER.value;
    }

    const index = studentList.findIndex((s) => s.get('studentBid') === studentBid);
    if (index < 0) {
        return null;
    } else if (studentList.size === 2 && index === 1) {
        // if on the last student return empty student placeholder
        return CommonValues.EMPTY_STUDENT_PLACEHOLDER.value;
    } else if (index + 1 >= studentList.size) {
        return studentList.get(0);
    }

    return studentList.get(index + 1);
};

export const latestItemByTerm = (itemWithTerm) => {
    // Assumes that items in the array/list have a property called 'termNumber'
    let latestItem = null;
    if (!itemWithTerm) {
        // Nothing was passed in (null or undefined)
        return null;
    } else if (itemWithTerm instanceof Array) {
        itemWithTerm.forEach((item) => {
            if (item.termNumber && (latestItem == null || item.termNumber > latestItem.termNumber)) {
                latestItem = item;
            }
        });
        return latestItem;
    } else if (itemWithTerm instanceof Immutable.List) {
        itemWithTerm.forEach((item) => {
            if (
                item.get('termNumber') &&
                (latestItem == null || item.get('termNumber') > latestItem.get('termNumber'))
            ) {
                latestItem = item;
            }
        });
        return latestItem;
    } else {
        throw new Error(
            'unexpected condition: item supplied should be an array or list, but instead was ' + typeof itemWithTerm
        );
    }
};

const getStudentData=(studentInfo, selectedTermNumber) =>{
    let selectedStudentInfo = null;
    if (selectedTermNumber == CommonValues.mostRecentTermNumber) {
        selectedStudentInfo = latestItemByTerm(studentInfo);
    } else {
        selectedStudentInfo = studentInfo
            ? studentInfo.find((si) => si.get('termNumber', '') == selectedTermNumber, null, null)
            : null;
    }
    if (__BATCH__ && !selectedStudentInfo) {
        selectedStudentInfo = latestItemByTerm(studentInfo);
    }
    const studentGrade = selectedStudentInfo ? selectedStudentInfo.get('gradeNweaName', '') : '';
    return {  studentGrade };
}

export const findPreviousStudent = function(studentList, studentBid) {
    if (!studentList || !studentBid) {
        return null;
    } else if (studentList.size < 2) {
        return CommonValues.EMPTY_STUDENT_PLACEHOLDER.value;
    }

    const index = studentList.findIndex((s) => s.get('studentBid') === studentBid);
    if (index < 0) {
        return null;
    } else if (studentList.size === 2 && index === 0) {
        // if on the first student return empty student placeholder
        return CommonValues.EMPTY_STUDENT_PLACEHOLDER.value;
    } else if (index === 0) {
        return studentList.get(studentList.size - 1);
    }
    return studentList.get(index - 1);
};

export const findMostRecentTestEventsBySubject = (testEvents) => {
    if (!testEvents || testEvents.isEmpty()) return null;

    return testEvents
        .groupBy((e) => e.get('subject'))
        .map((tes, subject) => {
            return tes.maxBy((e) => e.get('termNumber'));
        });
};

export const findTestEventForTermAndSubject = (testEvents, termNumber, subject) => {
    //Assuming testEvent is list
    if (!testEvents || testEvents.isEmpty()) {
        return null;
    }
    // This does not work with most recent term number of 900001 because a testEvent's termNumber will never be 900001
    return testEvents.find((t) => t.get('termNumber') == termNumber && t.get('subject') == subject, null, null);
};

export const buildRitQuintileKeyset = (studentTermData, subjects) => {
    const keyset = [];

    if (studentTermData && subjects) {
        // build up keyset values for each term and subject
        studentTermData.forEach((t) => {
            subjects.forEach((s) => {
                keyset.push({
                    termNumber: t.get('termNumber'),
                    subject: s.get('bid'),
                    grade: t.get('grade'),
                    weeks: t.get('weeks'),
                });
            });
        });
    }

    return keyset;
};

/**
 * currentRitQuintiles: from partner store,
 * terms: from partner store,
 * studentInfo: from student store,
 */
export const findMissingRitQuintileKeys = (currentRitQuintiles, studentTermData, subjects) => {
    // find all the keys that are needed
    const fullKeyset = buildRitQuintileKeyset(studentTermData, subjects);

    // filter out the ones that are in the store already
    return fullKeyset.filter((k) => {
        const key = toImmutable({
            termNumber: k.termNumber,
            subject: k.subject,
            grade: k.grade,
            weeks: k.weeks,
        });
        return !currentRitQuintiles.has(key);
    });
};

export const ritQuintilesLoaded = (currentRitQuintiles, studentTermData, subjects) => {
    const missingQuintileKeys = findMissingRitQuintileKeys(currentRitQuintiles, studentTermData, subjects);
    //if(missingQuintileKeys.length > 0) {
    //    console.log(`currentRitQuintiles.size ${currentRitQuintiles.size}`);
    //    console.log(`first missingQuintileKey: ${JSON.stringify(missingQuintileKeys[0], null, '\t')}`);
    //}
    return missingQuintileKeys.length === 0;
};

export const buildDistrictAverageKeyset = (studentTermData, subjects) => {
    const keyset = [];

    if (studentTermData && subjects) {
        studentTermData.forEach((t) => {
            subjects.forEach((s) => {
                keyset.push({
                    termNumber: t.get('termNumber'),
                    grade: t.get('grade'),
                    subject: s.get('bid'),
                });
            });
        });
    }

    return keyset;
};

export const findMissingDistrictAverageKeys = (studentTermData, subjects, currentDistrictAverages) => {
    // find all the keys that are needed
    const fullKeyset = buildDistrictAverageKeyset(studentTermData, subjects);

    // filter out the ones that are in the store already
    return fullKeyset.filter((k) => {
        const key = toImmutable({
            termNumber: k.termNumber,
            subject: k.subject,
            grade: k.grade,
        });
        return !currentDistrictAverages.has(key);
    });
};

export const districtAveragesLoaded = (currentDistrictAverages, studentTermData, subjects) => {
    const missingDAKeys = findMissingDistrictAverageKeys(studentTermData, subjects, currentDistrictAverages);
    return __DISABLE_DISTRICT_AVERAGES__ ? true : missingDAKeys.length === 0;
};

export const fieldLoadedForStudent = (studentList, studentBid, field) => {
    return studentList && studentList.hasIn([studentBid, field]);
};

export const growthGoalNormsLoaded = (studentList, growthGoalsNorms, subjects, studentBid, terms) => {
    if (!studentList || !terms || !fieldLoadedForStudent(studentList, studentBid, 'testEvents') || !growthGoalsNorms) {
        return false;
    }

    const testEvents = studentList.getIn([studentBid, 'testEvents']);
    const studentTermData = studentList.getIn([studentBid, 'termData']);
    const currentTerm = findCurrentTerm(terms);
    const studentInfo = studentList.getIn([studentBid, 'studentInfo']);
    const selectedTermNumber = studentList.getIn([studentBid, 'selectedTermNumber']);
    const { studentGrade }=getStudentData(studentInfo,selectedTermNumber);
    return subjects.reduce((currVal, s) => {
        if (!currVal) {
            return false;
        }
        const subjectNormsCoverage = CommonValues.growthSummaryProjectionNormCoverage[3];
        if(studentGrade >= subjectNormsCoverage.min && studentGrade <= subjectNormsCoverage.max){

        const firstOpenTerm = GrowthGoalsUtils.getFirstOpenTerm(terms, s.get('bid'), testEvents);
        const maxTestEventForSubject = GrowthGoalsUtils.findMaxTestEventForSubject(testEvents, s);
        const goalSettingTerms = GrowthGoalsUtils.findGoalSettingTerms(
            terms,
            firstOpenTerm,
            currentTerm,
            maxTestEventForSubject,
            studentTermData
        );

        return goalSettingTerms.reduce((currVal, t) => {
            return currVal && growthGoalsNorms.hasIn([s.get('bid'), t.get('termNumber')]);
        }, true);
    }else {
        return true;
    }
    }, true);
};
